import { DownloadSimple, Paperclip } from "@phosphor-icons/react";
import {
  ATTACHMENT_BLOCKED_REASON,
  ATTACHMENT_MEDIA_TYPE_LABEL,
  ATTACHMENT_STATUS_LABEL,
  ATTACHMENT_UNKNOWN_BLOCKED_REASON,
  formatAttachmentBytes,
  useAttachmentDownload,
  type ClientAttachment
} from "../data/attachmentQueries";
import { useViewOnly } from "../data/readSource";

/**
 * The files hanging off one chat message or one request comment, and the way to open them.
 *
 * plan.md sections 5.2 and 5.3 give the client protected attachments on both surfaces, and until
 * 2026-09-12 the only attachment read path in the application was Petrovic's. This is the client's,
 * and it is one component because the two surfaces show the same rows: `public.attachments` holds
 * both and `attachments_exactly_one_parent_check` is the only thing that separates them.
 *
 * **The download control is offered only for `available`.** That is the one status
 * `public.issue_attachment_download_ticket` will mint a ticket for, so a control on any other row
 * would spend one of the client's 30 tickets per ten minutes to be told no. Every other status gets
 * the sentence in place of the control rather than a greyed-out button beside it, which is the
 * decision Petrovic's own list made and the reason is the same: a disabled control says the file is
 * there and the portal is being unhelpful, and four of the five statuses mean genuinely different
 * things a person acts on differently.
 *
 * The status is a word. design.md's rule for the navigation rail is the rule here too, and nothing in
 * this list depends on colour to be read.
 *
 * One mutation for the whole list, and the row it belongs to is read off its variables. Without that,
 * one refused download would put the same red sentence under every file and disable every control at
 * once, and a client with four files would be told that four of them failed when one did.
 */

export interface AttachmentListProps {
  /** The tenant the reader is asking as. The Worker's body carries it, and its absence means admin. */
  tenantId: string;
  attachments: readonly ClientAttachment[];
  /**
   * What the list is called to a screen reader, in context.
   *
   * A bare "Bilagor" repeated once per message turns a conversation into a page of identical
   * landmarks, so the caller says which message or comment this one belongs to.
   */
  label: string;
  download: ReturnType<typeof useAttachmentDownload>;
}

export function AttachmentList({ tenantId, attachments, label, download }: AttachmentListProps) {
  // "Visa som kund" (build round 41): the list as the client sees it, without the way to the file.
  const viewOnly = useViewOnly();
  if (attachments.length === 0) {
    return null;
  }

  return (
    <ul className="attachment-list" aria-label={label} data-testid="attachment-list">
      {attachments.map((row) => {
        const openable = row.availability_status === "available";
        const inFlight = download.isPending && download.variables?.attachmentId === row.id;
        const failed = download.isError && download.variables?.attachmentId === row.id;

        return (
          <li
            key={row.id}
            className="attachment-row"
            data-availability={row.availability_status}
            data-testid="attachment-item"
          >
            <div className="attachment-row-main">
              <Paperclip size={18} aria-hidden="true" />
              <div className="attachment-row-text">
                <strong>{row.display_filename}</strong>
                <p className="attachment-meta">
                  <span>{ATTACHMENT_MEDIA_TYPE_LABEL[row.media_type] ?? row.media_type}</span>
                  <span>{formatAttachmentBytes(row.byte_size)}</span>
                  <span data-testid="attachment-status">
                    {ATTACHMENT_STATUS_LABEL[row.availability_status] ?? row.availability_status}
                  </span>
                </p>
              </div>

              {openable && !viewOnly ? (
                <button
                  type="button"
                  className="secondary-button attachment-open"
                  disabled={download.isPending}
                  data-testid="attachment-open"
                  onClick={() =>
                    download.mutate({
                      tenantId,
                      attachmentId: row.id,
                      filename: row.display_filename
                    })
                  }
                >
                  <DownloadSimple size={16} aria-hidden="true" />
                  {/*
                    The accessible name says which file. Four rows whose only control is called
                    "Öppna" is four identical buttons to anybody reading through the accessibility
                    tree, and the filename is the only thing that tells them apart.
                  */}
                  <span>{inFlight ? "Hämtar" : "Öppna"}</span>
                  <span className="visually-hidden">{row.display_filename}</span>
                </button>
              ) : null}
            </div>

            {/* The reason, in place of the control rather than beside a disabled one. */}
            {openable ? null : (
              <p className="attachment-blocked" data-testid="attachment-blocked">
                {ATTACHMENT_BLOCKED_REASON[row.availability_status] ??
                  ATTACHMENT_UNKNOWN_BLOCKED_REASON}
              </p>
            )}

            {failed ? (
              <p className="form-error" role="alert" data-testid="attachment-download-error">
                {download.error?.message}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
